/**
 * TarifsPage — grille tarifaire cantine / périscolaire, réservée maire/admin.
 *
 * Deux types de lignes :
 *   - Tarif plein : sans bornes de QF (appliqué si pas de QF ou si aucune tranche ne matche).
 *   - Tranche QF : bornes incluses (QF mensuel) → prix du repas.
 * Le coefficient de majoration s'applique aux repas pris sans réservation quand
 * la politique du tenant est « facturé majoré » (réglée dans Paramètres).
 *
 * Montants saisis en euros, convertis en centimes pour l'API.
 */

import { useEffect, useState, useCallback } from 'react';
import { Euro, Plus, Trash2, Save, AlertCircle } from 'lucide-react';
import { api, ApiException } from '@/lib/api';
import { Card, CardBody, Button, Field, Input, Alert, Badge } from '@/components/ui';
import { ScolaireLayout } from '@/components/scolaire/ScolaireLayout';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import type { RestaurationTarif, ServiceFacturable } from '@roronoa-collectivites/shared-types';

const SERVICES: { value: ServiceFacturable; label: string }[] = [
  { value: 'cantine', label: 'Cantine' },
  { value: 'periscolaire', label: 'Périscolaire' },
  { value: 'alsh', label: 'ALSH' },
];

const euros = (centimes: number) => (centimes / 100).toFixed(2);

interface DraftTarif {
  service: ServiceFacturable;
  libelle: string;
  type: 'plein' | 'tranche';
  qfMin: string;
  qfMax: string;
  prixEuros: string;
  coefMajoration: string;
  modeTarif: 'forfait' | 'temps';
  prixUniteEuros: string;
  forfaitMinEuros: string;
  prixDemiJourneeEuros: string;  // ALSH forfait demi-journée
  prixRepasAlshEuros: string;    // ALSH repas séparé
}
const draftVide = (service: ServiceFacturable): DraftTarif => ({
  service, libelle: '', type: 'tranche', qfMin: '', qfMax: '', prixEuros: '', coefMajoration: '1',
  modeTarif: 'forfait', prixUniteEuros: '', forfaitMinEuros: '', prixDemiJourneeEuros: '', prixRepasAlshEuros: '',
});

export function TarifsPage() {
  const [service, setService] = useState<ServiceFacturable>('cantine');
  const [tarifs, setTarifs] = useState<RestaurationTarif[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftTarif>(draftVide('cantine'));
  const [saving, setSaving] = useState(false);
  const { confirm, dialogElement } = useConfirmDialog();

  const charger = useCallback(async (svc: ServiceFacturable) => {
    setLoading(true); setError(null);
    try { setTarifs(await api.scolaire.facturation.listTarifs(svc)); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur de chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void charger(service); }, [service, charger]);

  async function ajouter() {
    setSaving(true); setError(null); setOk(null);
    try {
      const euro = (s: string) => Math.round(parseFloat(s.replace(',', '.')) * 100);
      // ALSH : repas séparé (optionnel), ajouté quel que soit le mode d'accueil.
      const repasAlsh = draft.service === 'alsh' && draft.prixRepasAlshEuros.trim() !== '' ? euro(draft.prixRepasAlshEuros) : null;
      const commun = {
        service: draft.service,
        libelle: draft.libelle || (draft.type === 'plein' ? 'Tarif plein' : 'Tranche QF'),
        qfMin: draft.type === 'plein' ? null : (draft.qfMin === '' ? null : parseInt(draft.qfMin, 10)),
        qfMax: draft.type === 'plein' ? null : (draft.qfMax === '' ? null : parseInt(draft.qfMax, 10)),
        modeTarif: draft.modeTarif,
        prixRepasAlsh: repasAlsh,
        actif: true,
      };
      if (draft.modeTarif === 'temps') {
        const pu = euro(draft.prixUniteEuros);
        if (!Number.isFinite(pu) || pu < 0) throw new Error('Prix à l’unité invalide');
        const fmin = draft.forfaitMinEuros.trim() === '' ? null : euro(draft.forfaitMinEuros);
        await api.scolaire.facturation.createTarif({ ...commun, prixUnite: pu, forfaitMinimum: fmin });
      } else {
        const prix = euro(draft.prixEuros);
        if (!Number.isFinite(prix) || prix < 0) throw new Error('Prix invalide');
        // ALSH forfait : prixRepas = prix journée ; prixDemiJournee = forfait matin/après-midi.
        const demi = draft.service === 'alsh' && draft.prixDemiJourneeEuros.trim() !== '' ? euro(draft.prixDemiJourneeEuros) : null;
        await api.scolaire.facturation.createTarif({
          ...commun, prixRepas: prix, prixDemiJournee: demi,
          coefMajoration: parseFloat(draft.coefMajoration.replace(',', '.')) || 1,
        });
      }
      setDraft(draftVide(draft.service));
      setOk('Tarif ajouté.');
      await charger(service);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : e instanceof Error ? e.message : 'Erreur');
    } finally { setSaving(false); }
  }

  async function supprimer(t: RestaurationTarif) {
    const yes = await confirm({
      title: 'Supprimer ce tarif ?',
      description: `« ${t.libelle} » sera retiré de la grille. Les factures déjà émises ne changent pas.`,
      confirmLabel: 'Supprimer', danger: true,
    });
    if (!yes) return;
    try { await api.scolaire.facturation.deleteTarif(t.id); await charger(service); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  const libelleTranche = (t: RestaurationTarif) => {
    if (t.qfMin == null && t.qfMax == null) return 'Tarif plein (sans QF)';
    if (t.qfMin != null && t.qfMax != null) return `QF ${t.qfMin} – ${t.qfMax}`;
    if (t.qfMin != null) return `QF ≥ ${t.qfMin}`;
    return `QF ≤ ${t.qfMax}`;
  };

  return (
    <ScolaireLayout activeTab="tarifs">
      <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6 space-y-5">
        <div className="flex items-center gap-3">
          <span className="inline-grid h-10 w-10 place-items-center rounded-r-lg bg-cantine-tint text-cantine-fg" aria-hidden="true">
            <Euro className="h-5 w-5" />
          </span>
          <div>
            <p className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Scolaire / Cantine</p>
            <h1 className="font-display text-xl font-semibold text-ink sm:text-2xl">Grille tarifaire</h1>
          </div>
        </div>

        {/* Sélecteur de service */}
        <div className="flex gap-2">
          {SERVICES.map((s) => (
            <button key={s.value} type="button" onClick={() => { setService(s.value); setDraft((d) => ({ ...d, service: s.value })); }}
              className={['rounded-r-md border px-3 py-1.5 text-sm font-medium transition-colors',
                service === s.value ? 'border-cantine bg-cantine/10 text-cantine-fg' : 'border-border text-ink-muted hover:text-ink'].join(' ')}>
              {s.label}
            </button>
          ))}
        </div>

        {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
        {ok && <Alert variant="success">{ok}</Alert>}

        {/* Liste des tarifs */}
        <Card>
          <CardBody>
            <h2 className="mb-3 font-display text-base font-semibold text-ink">Tarifs en vigueur</h2>
            {loading ? (
              <p className="text-sm text-ink-muted">Chargement…</p>
            ) : tarifs.length === 0 ? (
              <p className="text-sm text-ink-muted">Aucun tarif défini pour ce service. Ajoutez au moins un tarif plein ci-dessous.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-2xs uppercase tracking-wide text-ink-subtle">
                    <th className="py-2">Libellé</th>
                    <th className="py-2">Tranche</th>
                    <th className="py-2 text-right">Prix repas</th>
                    <th className="py-2 text-right">Coef. majoré</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {tarifs.map((t) => (
                    <tr key={t.id} className="border-b border-border/60">
                      <td className="py-2 text-ink">{t.libelle} {!t.actif && <Badge variant="warning">inactif</Badge>}</td>
                      <td className="py-2 text-ink-muted">{libelleTranche(t)}</td>
                      <td className="py-2 text-right font-semibold tabular-nums text-ink">
                        {t.modeTarif === 'temps'
                          ? `${euros(t.prixUnite ?? 0)} €/u${t.forfaitMinimum ? ` (min ${euros(t.forfaitMinimum)} €)` : ''}`
                          : `${euros(t.prixRepas)} €`}
                      </td>
                      <td className="py-2 text-right tabular-nums text-ink-muted">{t.modeTarif === 'temps' ? '—' : `×${t.coefMajoration.toFixed(2)}`}</td>
                      <td className="py-2 text-right">
                        <button type="button" onClick={() => void supprimer(t)} aria-label="Supprimer"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-r-md text-ink-muted hover:bg-danger/10 hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardBody>
        </Card>

        {/* Ajout d'un tarif */}
        <Card>
          <CardBody>
            <h2 className="mb-3 font-display text-base font-semibold text-ink">Ajouter un tarif</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Type" htmlFor="t-type">
                <select id="t-type" value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value as 'plein' | 'tranche' })}
                  className="w-full rounded-r-md border border-border bg-surface px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cantine">
                  <option value="tranche">Tranche de quotient familial</option>
                  <option value="plein">Tarif plein (sans QF)</option>
                </select>
              </Field>
              <Field label="Libellé" htmlFor="t-lib">
                <Input id="t-lib" value={draft.libelle} onChange={(e) => setDraft({ ...draft, libelle: e.target.value })}
                  placeholder={draft.type === 'plein' ? 'Tarif plein' : 'ex. QF 0–700'} />
              </Field>
              {draft.type === 'tranche' && (
                <>
                  <Field label="QF minimum (€)" htmlFor="t-min">
                    <Input id="t-min" type="number" min={0} value={draft.qfMin} onChange={(e) => setDraft({ ...draft, qfMin: e.target.value })} placeholder="0" />
                  </Field>
                  <Field label="QF maximum (€)" htmlFor="t-max">
                    <Input id="t-max" type="number" min={0} value={draft.qfMax} onChange={(e) => setDraft({ ...draft, qfMax: e.target.value })} placeholder="700" />
                  </Field>
                </>
              )}
              <Field label="Mode de facturation" htmlFor="t-mode">
                <select id="t-mode" value={draft.modeTarif} onChange={(e) => setDraft({ ...draft, modeTarif: e.target.value as 'forfait' | 'temps' })}
                  className="w-full rounded-r-md border border-border bg-surface px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cantine">
                  <option value="forfait">Forfait (au repas / créneau)</option>
                  <option value="temps">Au temps de présence (entrée/sortie)</option>
                </select>
              </Field>
              {draft.modeTarif === 'forfait' ? (
                <>
                  <Field label={draft.service === 'alsh' ? 'Prix journée (€)' : 'Prix du repas (€)'} htmlFor="t-prix">
                    <Input id="t-prix" inputMode="decimal" value={draft.prixEuros} onChange={(e) => setDraft({ ...draft, prixEuros: e.target.value })} placeholder="3.50" />
                  </Field>
                  {draft.service === 'alsh' && (
                    <Field label="Prix demi-journée (€)" htmlFor="t-demi">
                      <Input id="t-demi" inputMode="decimal" value={draft.prixDemiJourneeEuros} onChange={(e) => setDraft({ ...draft, prixDemiJourneeEuros: e.target.value })} placeholder="ex. 5.00" />
                    </Field>
                  )}
                  {draft.service !== 'alsh' && (
                    <Field label="Coefficient majoration (repas non réservé)" htmlFor="t-coef">
                      <Input id="t-coef" inputMode="decimal" value={draft.coefMajoration} onChange={(e) => setDraft({ ...draft, coefMajoration: e.target.value })} placeholder="1.00" />
                    </Field>
                  )}
                </>
              ) : (
                <>
                  <Field label="Prix par unité de temps (€)" htmlFor="t-unite">
                    <Input id="t-unite" inputMode="decimal" value={draft.prixUniteEuros} onChange={(e) => setDraft({ ...draft, prixUniteEuros: e.target.value })} placeholder="0.50" />
                  </Field>
                  <Field label="Forfait minimum (€) — optionnel" htmlFor="t-fmin">
                    <Input id="t-fmin" inputMode="decimal" value={draft.forfaitMinEuros} onChange={(e) => setDraft({ ...draft, forfaitMinEuros: e.target.value })} placeholder="laisser vide si aucun" />
                  </Field>
                </>
              )}
              {draft.service === 'alsh' && (
                <Field label="Prix du repas (€) — ajouté si l'enfant mange" htmlFor="t-repas-alsh">
                  <Input id="t-repas-alsh" inputMode="decimal" value={draft.prixRepasAlshEuros} onChange={(e) => setDraft({ ...draft, prixRepasAlshEuros: e.target.value })} placeholder="ex. 3.50 — laisser vide si aucun" />
                </Field>
              )}
            </div>
            <div className="mt-4">
              <Button variant="cantine" loading={saving} onClick={() => void ajouter()}>
                <Plus className="h-4 w-4" /> Ajouter le tarif
              </Button>
            </div>
          </CardBody>
        </Card>
      </main>
      {dialogElement}
    </ScolaireLayout>
  );
}
